import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Storefront, FileText, DownloadSimple, BookOpen, Warning, Trash } from "@phosphor-icons/react";
import { API, getStoredToken } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC"
];

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const isDemo = user?.role === "demo" || user?.is_demo;
  const [p, setP] = useState({
    brand_name: "",
    logo_data: "",
    legal_entity: "",
    responsible_party_prefix: "Distributed by",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "USA",
    website: "",
    default_mesh_size: "",
    default_shelf_life_months: "",
    default_storage_conditions: "",
    default_packaging: "",
    default_micro_spec: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.business_profile) {
      setP((prev) => ({ ...prev, ...user.business_profile }));
    }
  }, [user]);

  const set = (k) => (e) => setP({ ...p, [k]: e.target?.value ?? e });
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", p);
      await refreshUser();
      toast.success("Business profile saved — labels will now include your branding");
    } catch (err) {
      console.error("Business profile save failed", err);
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Settings</div>
        <h1 className="text-4xl font-bold tracking-tight">Business Profile</h1>
        <p className="text-stone-600 mt-2">
          These details print on every label you export — per 21 CFR 101.5, every commercial food
          label must include the name and place of business of the manufacturer, packer, or distributor.
        </p>

        <Card className="p-6 mt-6 border-stone-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-md bg-emerald-50 text-emerald-800 grid place-items-center">
              <Storefront size={18} weight="duotone" />
            </div>
            <h2 className="text-xl font-semibold">Identity</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Brand name (shown on jar front)" testId="brand-name">
              <Input value={p.brand_name} onChange={set("brand_name")}
                placeholder={isDemo ? "Your brand name" : "Alaine & Co."} data-testid="brand-name-input" />
            </Field>
            <Field label="Legal entity / company name" testId="legal-entity">
              <Input value={p.legal_entity} onChange={set("legal_entity")}
                placeholder={isDemo ? "Your LLC / Inc." : "Shirleys Daughter, LLC"} data-testid="legal-entity-input" />
            </Field>
            <Field label="Responsible party prefix (FDA required)">
              <Select value={p.responsible_party_prefix} onValueChange={(v) => setP({ ...p, responsible_party_prefix: v })}>
                <SelectTrigger data-testid="prefix-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Distributed by">Distributed by</SelectItem>
                  <SelectItem value="Manufactured by">Manufactured by</SelectItem>
                  <SelectItem value="Manufactured for">Manufactured for</SelectItem>
                  <SelectItem value="Packed by">Packed by</SelectItem>
                  <SelectItem value="Packed for">Packed for</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Website (optional)">
              <Input value={p.website} onChange={set("website")}
                placeholder={isDemo ? "yourbrand.com" : "alainesfield.com"} data-testid="website-input" />
            </Field>
            <Field label="Brand logo (printed at the top of your labels)">
              <div className="flex items-center gap-3">
                {p.logo_data ? (
                  <img src={p.logo_data} alt="Brand logo" className="h-12 w-12 object-contain border border-stone-200 rounded bg-white p-1" data-testid="logo-preview" />
                ) : (
                  <div className="h-12 w-12 border border-dashed border-stone-300 rounded flex items-center justify-center text-[9px] text-stone-400 text-center leading-tight">No logo</div>
                )}
                <div className="flex flex-col gap-1">
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="text-xs" data-testid="logo-upload-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 4 * 1024 * 1024) { toast.error("Logo file is too large (max 4MB)"); return; }
                      const reader = new FileReader();
                      reader.onload = () => {
                        const img = new Image();
                        img.onload = () => {
                          // Downscale to max 600px on the long edge, keep transparency (PNG)
                          const scale = Math.min(1, 600 / Math.max(img.width, img.height));
                          const canvas = document.createElement("canvas");
                          canvas.width = Math.round(img.width * scale);
                          canvas.height = Math.round(img.height * scale);
                          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                          const dataUrl = canvas.toDataURL("image/png");
                          if (dataUrl.length > 680_000) { toast.error("Logo is too detailed to store — try a simpler or smaller image"); return; }
                          setP((prev) => ({ ...prev, logo_data: dataUrl }));
                          toast.success("Logo ready — click Save to keep it");
                        };
                        img.onerror = () => toast.error("Could not read that image");
                        img.src = reader.result;
                      };
                      reader.readAsDataURL(file);
                      e.target.value = "";
                    }} />
                  {p.logo_data && (
                    <button type="button" className="text-xs text-red-700 underline text-left" data-testid="logo-remove-button"
                      onClick={() => setP((prev) => ({ ...prev, logo_data: "" }))}>
                      Remove logo
                    </button>
                  )}
                </div>
              </div>
            </Field>
          </div>

          <h2 className="text-xl font-semibold mt-8 mb-4">Address</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Street address">
              <Input value={p.address_line1} onChange={set("address_line1")} data-testid="address1-input" />
            </Field>
            <Field label="Apt / suite / unit (optional)">
              <Input value={p.address_line2} onChange={set("address_line2")} data-testid="address2-input" />
            </Field>
            <Field label="City">
              <Input value={p.city} onChange={set("city")} data-testid="city-input" />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="State">
                <Select value={p.state} onValueChange={(v) => setP({ ...p, state: v })}>
                  <SelectTrigger data-testid="state-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="ZIP">
                <Input value={p.zip_code} onChange={set("zip_code")} data-testid="zip-input" />
              </Field>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-md bg-stone-50 border border-stone-200 font-fda text-xs text-black">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-sans mb-1">Label preview</div>
            {p.responsible_party_prefix} {p.legal_entity || "[Your Legal Entity]"}
            <br />{p.address_line1 || "[Street address]"}{p.address_line2 ? `, ${p.address_line2}` : ""}
            <br />{p.city || "[City]"}, {p.state || "[ST]"} {p.zip_code || "[ZIP]"}{p.country ? ` ${p.country}` : ""}
            {p.website && <><br />{p.website}</>}
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={save} disabled={saving}
              className="bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="save-profile-button">
              {saving ? "Saving…" : "Save business profile"}
            </Button>
          </div>
        </Card>

        <Card className="p-6 border-stone-200 mt-6" data-testid="spec-defaults-card">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={18} className="text-emerald-700" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Spec Sheet Defaults</div>
              <h2 className="text-xl font-semibold mt-0.5">Pre-fill values for new formulas</h2>
            </div>
          </div>
          <p className="text-sm text-stone-600 leading-relaxed mb-4">
            Set your standard physical and microbiological spec values once. They'll auto-fill into every
            new formula's Spec Sheet card so you don't retype them. You can override per-formula.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Default mesh / particle size">
              <Input value={p.default_mesh_size} onChange={set("default_mesh_size")}
                placeholder="80 mesh, 95% pass-through" data-testid="default-mesh-input" />
            </Field>
            <Field label="Default shelf life (months)">
              <Input type="number" step="1" value={p.default_shelf_life_months} onChange={set("default_shelf_life_months")}
                placeholder="12" data-testid="default-shelflife-input" />
              <div className="text-[11px] text-stone-500 mt-1.5 leading-relaxed bg-stone-50 border border-stone-200 rounded p-2">
                <b className="text-stone-700">Per-formula exceptions to consider overriding:</b>
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  <li><b>Pure salt + low-aromatic blends</b> (paprika, garlic, cumin, chiles) → up to <b>24 months</b></li>
                  <li><b>Citrus / mint / vanilla forward blends</b> → keep at <b>12 months</b> (default)</li>
                  <li><b>Cheese, butter, or real-oil flavor blends</b> → reduce to <b>9 months</b></li>
                </ul>
              </div>
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Default storage conditions">
              <Input value={p.default_storage_conditions} onChange={set("default_storage_conditions")}
                placeholder="Cool, dry · <75°F · <60% RH" data-testid="default-storage-input" />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Default packaging">
              <Input value={p.default_packaging} onChange={set("default_packaging")}
                placeholder="25 lb double-poly bag in fiber drum" data-testid="default-packaging-input" />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Default microbiological spec">
              <textarea value={p.default_micro_spec} onChange={set("default_micro_spec")}
                placeholder="TPC <100,000 CFU/g · Yeast & Mold <1,000 CFU/g · E. coli negative · Salmonella negative · Listeria negative"
                className="w-full min-h-[70px] p-2 rounded-md border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="default-micro-input"/>
            </Field>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={save} disabled={saving}
              className="bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="save-spec-defaults-button">
              {saving ? "Saving…" : "Save spec defaults"}
            </Button>
          </div>
        </Card>

        <Card className="p-6 border-stone-200 mt-6" data-testid="user-guide-card">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen size={18} className="text-emerald-700" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500">User Resources</div>
              <h2 className="text-xl font-semibold mt-0.5">User Guide & SOP</h2>
            </div>
          </div>
          <p className="text-sm text-stone-600 leading-relaxed mb-4">
            Step-by-step Standard Operating Procedure covering every workflow — formulation building, costing,
            application calculator, formulation flags, ingredient intelligence, version control, printing,
            and the AI compliance assistant. Hand this to future paying users so they can
            self-onboard.
          </p>
          {isDemo ? (
            <div className="text-sm text-stone-500 italic" data-testid="user-guide-demo-locked">
              Available to paying customers. <a href="/register" className="text-emerald-800 underline font-medium">Sign up →</a>
            </div>
          ) : (
            <>
              <Button onClick={() => downloadDoc("/legal/user-sop", "AlainesBench_User_Guide_SOP.md")} variant="outline"
                className="border-emerald-700 text-emerald-800 hover:bg-emerald-50"
                data-testid="download-sop-btn">
                <DownloadSimple size={16} className="mr-2" /> Download User Guide / SOP (.md)
              </Button>
              <Button onClick={() => downloadDoc("/legal/lab-qa-sop", "AlainesBench_Lab_QA_Procedures_SOP.md")} variant="outline"
                className="border-emerald-700 text-emerald-800 hover:bg-emerald-50 ml-2"
                data-testid="download-lab-sop-btn">
                <DownloadSimple size={16} className="mr-2" /> Lab QA Procedures (.md)
              </Button>
            </>
          )}
        </Card>

        {/* DATA MIGRATION — preview ↔ production */}
        {!isDemo && <DataMigrationCard />}

        {!isDemo && (
        <Card className="p-6 border-stone-200 mt-6" data-testid="legal-docs-card">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={18} className="text-emerald-700" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Legal Toolkit</div>
              <h2 className="text-xl font-semibold mt-0.5">Protect your IP</h2>
            </div>
          </div>
          <p className="text-sm text-stone-600 leading-relaxed mb-5">
            Six documents to safeguard Alaine's Bench and prepare for funder/partner meetings.
            <b> Templates only — have a qualified attorney customize and approve before relying on them.</b>
          </p>
          <div className="grid sm:grid-cols-1 gap-3">
            <DocCard
              title="🎤 Founder Demo Script (5-min walkthrough + Q&A prep)"
              desc="Ready-to-read script for your next demo: pre-call checklist, 7-minute walkthrough beat-by-beat, recovery moves if anything glitches, and 8 likely Q&A questions with founder-grade answers. Built for the meeting you're about to take."
              testId="download-demo-script-btn"
              onClick={() => downloadDoc("/legal/demo-script", "AlainesBench_Demo_Script.md")}
            />
            <DocCard
              title="📋 IP Protection Checklist"
              desc="Step-by-step plan to protect your code, brand, and trade secrets — phased by cost (free → $5K). Includes copyright filing instructions, trademark guidance, and recommended attorneys."
              testId="download-checklist-btn"
              onClick={() => downloadDoc("/legal/ip-checklist", "AlainesBench_IP_Protection_Checklist.md")}
            />
            <DocCard
              title="🛡️ Trademark Filing Prep Sheet — `ALAINE'S BENCH`"
              desc="One-page USPTO TEAS Plus walkthrough: Class 9 + 42 goods/services descriptions (already on the approved ID Manual list), specimen instructions, knockout-search checklist, costs ($250–$500 DIY), and 10–14 month timeline. Hand to a trademark attorney or DIY-file."
              testId="download-trademark-prep-btn"
              onClick={() => downloadDoc("/legal/trademark-prep", "AlainesBench_Trademark_Filing_Prep.md")}
            />
            <DocCard
              title="📑 Copyright Deposit — SOFTWARE"
              desc="Ready-to-upload deposit file for Copyright Filing #1. Upload this as the 'deposit copy' when filing your $65 Software copyright on copyright.gov."
              testId="download-copyright-software-btn"
              onClick={() => downloadDoc("/legal/copyright-software-deposit", "AlainesBench_Software_Deposit.txt")}
            />
            <DocCard
              title="📑 Copyright Deposit — CONTENT"
              desc="Ready-to-upload deposit file for Copyright Filing #2. Upload this as the 'deposit copy' when filing your $65 Content/Database copyright on copyright.gov."
              testId="download-copyright-content-btn"
              onClick={() => downloadDoc("/legal/copyright-content-deposit", "AlainesBench_Content_Deposit.txt")}
            />
            <DocCard
              title="🔒 Trade Secret Inventory (CONFIDENTIAL)"
              desc="Dated record of your proprietary IP — required by federal trade-secret law to prove you took reasonable measures. Save to your private cloud; do NOT share."
              testId="download-inventory-btn"
              onClick={() => downloadDoc("/legal/trade-secret-inventory", "AlainesBench_Trade_Secret_Inventory_CONFIDENTIAL.md")}
            />
            <DocCard
              title="🤝 Tester NDA — 1-Page · Illinois (current)"
              desc="Use this for any NDA you send while your LLC is still registered in Illinois. Includes a 'supersedes any prior NDA' clause for your tester who already signed Friday's longer version. Just fill in 3 fields and re-send."
              testId="download-nda-one-page-btn"
              onClick={() => downloadDoc("/legal/beta-nda-one-page", "AlainesBench_Tester_NDA_OnePage_IL.md")}
            />
            <DocCard
              title="🗽 Tester NDA — 1-Page · New Jersey (post-move)"
              desc="Same 1-pager but governed by NJ law / Essex County venue + N.J.S.A. 56:15-1 trade secret act. Use AFTER you register Shirleys Daughter, LLC in NJ as a foreign LLC (~$125, 1 form)."
              testId="download-nda-one-page-nj-btn"
              onClick={() => downloadDoc("/legal/beta-nda-one-page-nj", "AlainesBench_Tester_NDA_OnePage_NJ.md")}
            />
            <DocCard
              title="🏭 Co-Packer / Vendor Mutual NDA · Illinois (current)"
              desc="Send BEFORE sharing any formulation with a co-packer, contract chef, broker, lab, or investor. Protects formulas as trade secrets, blocks reverse-engineering, prohibits patent filings, includes 2-year supplier non-solicitation. Illinois law / Kane County."
              testId="download-copacker-nda-btn"
              onClick={() => downloadDoc("/legal/copacker-nda", "AlainesBench_CoPacker_Vendor_Mutual_NDA_IL.md")}
            />
            <DocCard
              title="🗽 Co-Packer / Vendor Mutual NDA · New Jersey (post-move)"
              desc="Same protections, NJ law / Essex County venue, N.J.S.A. 56:15-1 references. Use AFTER NJ LLC registration."
              testId="download-copacker-nda-nj-btn"
              onClick={() => downloadDoc("/legal/copacker-nda-nj", "AlainesBench_CoPacker_Vendor_Mutual_NDA_NJ.md")}
            />
            <DocCard
              title="🤝 Tester NDA — Full Version (legacy)"
              desc="Longer mutual NDA (10+ pages). Use only if your tester is a company with attorneys who'll demand a fuller version. For most testers the 1-page version above is enough."
              testId="download-nda-btn"
              onClick={() => downloadDoc("/legal/beta-nda", "AlainesBench_Tester_NDA_Template.md")}
            />
          </div>
        </Card>
        )}

        {/* DANGER ZONE — account deletion (GDPR Art. 17) */}
        {!isDemo && <DangerZoneCard />}
      </div>
    </Layout>
  );
}

function DocCard({ title, desc, onClick, testId }) {
  return (
    <div className="rounded-md border border-stone-200 bg-stone-50 p-4 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-stone-900">{title}</div>
        <div className="text-xs text-stone-600 mt-1 leading-relaxed">{desc}</div>
      </div>
      <Button onClick={onClick} variant="outline" size="sm"
        className="border-emerald-700 text-emerald-800 hover:bg-emerald-50 shrink-0"
        data-testid={testId}>
        <DownloadSimple size={14} className="mr-1.5" /> Download
      </Button>
    </div>
  );
}

function DataMigrationCard() {
  const [importing, setImporting] = useState(false);
  const [strategy, setStrategy] = useState("skip_existing");
  const [lastSummary, setLastSummary] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);

  const loadBackups = async () => {
    setLoadingBackups(true);
    try {
      const { data } = await api.get("/account/backups");
      setBackups(data.backups || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBackups(false);
    }
  };
  useEffect(() => { loadBackups(); }, []);

  const snapshotNow = async () => {
    try {
      await api.post("/account/backups/snapshot");
      toast.success("Snapshot created");
      loadBackups();
    } catch (err) {
      console.error(err);
      toast.error("Snapshot failed");
    }
  };

  const restoreSnapshot = async (id) => {
    if (!confirm("Restore this backup? Existing formulations with the same ID will be overwritten.")) return;
    try {
      const { data } = await api.post(`/account/backups/${id}/restore`);
      const s = data.summary || {};
      toast.success(`Restored · ${s.recipes || 0} formulations, ${s.ingredients || 0} ingredients`);
    } catch (err) {
      console.error(err);
      toast.error("Restore failed");
    }
  };

  const downloadSnapshot = async (id, createdAt) => {
    try {
      const token = getStoredToken();
      const resp = await fetch(`${API}/account/backups/${id}/download`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AlainesBench_Backup_${createdAt?.slice(0, 10) || "snapshot"}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const exportNow = async () => {
    try {
      const token = getStoredToken();
      const resp = await fetch(`${API}/account/export`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error("Server " + resp.status);
      const blob = await resp.blob();
      const filename = `AlainesBench_Backup_${new Date().toISOString().slice(0, 10)}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded — keep this file safe");
    } catch (err) {
      console.error(err);
      toast.error("Export failed — try again");
    }
  };

  const importFile = async (file) => {
    if (!file) return;
    setImporting(true);
    setLastSummary(null);
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      const { data } = await api.post("/account/import", {
        bundle,
        merge_strategy: strategy,
        overwrite_business_profile: true,
      });
      setLastSummary(data);
      const s = data.summary || {};
      const total = (s.ingredients || 0) + (s.recipes || 0) + (s.supplier_specs || 0) + (s.lots || 0);
      toast.success(`Imported ${total} items · ${s.recipes || 0} formulations restored`);
      loadBackups();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail
          ? `Import failed: ${err.response.data.detail}`
          : "Import failed — file may be corrupted or wrong format"
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card className="p-6 border-stone-200 mt-6" data-testid="data-migration-card">
      <div className="flex items-center gap-2 mb-2">
        <DownloadSimple size={18} className="text-emerald-700" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Data Backup & Migration</div>
          <h2 className="text-xl font-semibold mt-0.5">Your formulas are auto-backed-up</h2>
        </div>
      </div>
      <p className="text-sm text-stone-600 leading-relaxed mb-4">
        Every time you save a formulation, the system stores a complete snapshot on the server.
        We keep the <b>last 14 backups</b> automatically — no need to remember anything. Restore
        any of them with one click.
        <br />
        <span className="text-xs text-stone-500">
          A "JSON file" is just a structured text file — like a digital notebook a computer can read back perfectly. Treat it like a backup of a formulation binder.
        </span>
      </p>

      {/* AUTO-BACKUP HISTORY */}
      <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-[0.18em] text-emerald-900 font-semibold">
            Backup history (automatic)
          </div>
          <Button onClick={snapshotNow} variant="outline" size="sm"
            className="border-emerald-700 text-emerald-800 hover:bg-emerald-50"
            data-testid="snapshot-now-btn">
            Snapshot now
          </Button>
        </div>
        {loadingBackups && <div className="text-xs text-stone-500">Loading…</div>}
        {!loadingBackups && backups.length === 0 && (
          <div className="text-xs text-stone-500 py-2">
            No backups yet. The next time you save a formulation, one will be created automatically.
          </div>
        )}
        {!loadingBackups && backups.length > 0 && (
          <div className="space-y-1.5" data-testid="backup-history-list">
            {backups.map((b) => {
              const dt = new Date(b.created_at);
              return (
                <div key={b.id} className="flex items-center gap-3 py-1.5 px-2 rounded bg-white/70 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-stone-800">
                      {dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      <span className="ml-2 text-[10px] uppercase text-stone-500">
                        {b.trigger === "manual" ? "manual" : "auto"}
                      </span>
                    </div>
                    <div className="text-[10px] text-stone-500">
                      {b.counts?.recipes || 0} formulations · {b.counts?.ingredients_total || 0} ingredients ·
                      {" "}{b.counts?.starter_recipes || 0} starters
                    </div>
                  </div>
                  <button onClick={() => downloadSnapshot(b.id, b.created_at)}
                    className="text-[11px] text-stone-700 hover:text-emerald-800 underline"
                    data-testid={`download-backup-${b.id}`}>
                    Download
                  </button>
                  <button onClick={() => restoreSnapshot(b.id)}
                    className="text-[11px] text-emerald-800 font-semibold hover:underline"
                    data-testid={`restore-backup-${b.id}`}>
                    Restore
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="text-xs uppercase tracking-[0.18em] text-stone-600 font-semibold mb-2 mt-2">
        Move between preview & alainesbench.com (manual)
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-md border border-stone-200 bg-stone-50/60 p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-700 font-semibold mb-1">
            Step 1 · Download backup
          </div>
          <p className="text-xs text-stone-600 mb-3">
            Saves a single <code>.json</code> file with everything (215 ingredients, 15 starters, your formulations, supplier specs, lots, prices, profile).
          </p>
          <Button onClick={exportNow} className="bg-emerald-800 hover:bg-emerald-700 text-white"
            data-testid="export-data-btn">
            <DownloadSimple size={16} className="mr-2" /> Download my backup
          </Button>
        </div>

        <div className="rounded-md border border-stone-200 bg-stone-50/60 p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-700 font-semibold mb-1">
            Step 2 · Restore on the other side
          </div>
          <p className="text-xs text-stone-600 mb-3">
            Sign in to the other environment, open this card, and upload the file.
          </p>
          <div className="mb-2">
            <Label className="text-[11px] uppercase tracking-wider text-stone-500">If a formulation already exists…</Label>
            <Select value={strategy} onValueChange={setStrategy}>
              <SelectTrigger className="mt-1" data-testid="merge-strategy-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="skip_existing">Skip it (safe)</SelectItem>
                <SelectItem value="overwrite_existing">Overwrite with backup version</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="block">
            <input type="file" accept="application/json,.json"
              onChange={(e) => importFile(e.target.files?.[0])}
              disabled={importing}
              className="block w-full text-xs text-stone-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-emerald-700 file:text-white hover:file:bg-emerald-800 file:cursor-pointer cursor-pointer"
              data-testid="import-data-input"
            />
          </label>
          {importing && <div className="text-xs text-stone-500 mt-2">Importing…</div>}
        </div>
      </div>

      {lastSummary && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs"
          data-testid="import-summary">
          <div className="font-semibold text-emerald-900 mb-1">
            Restored from {lastSummary.imported_from} (exported {lastSummary.exported_at?.slice(0, 16)?.replace("T", " ")})
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-stone-700">
            <div>Ingredients: <b>{lastSummary.summary?.ingredients}</b> {lastSummary.summary?.skipped_ingredients ? `(${lastSummary.summary.skipped_ingredients} skipped)` : ""}</div>
            <div>Formulations: <b>{lastSummary.summary?.recipes}</b> {lastSummary.summary?.skipped_recipes ? `(${lastSummary.summary.skipped_recipes} skipped)` : ""}</div>
            <div>Starter templates: <b>{lastSummary.summary?.starter_recipes ?? 0}</b> {lastSummary.summary?.skipped_starter_recipes ? `(${lastSummary.summary.skipped_starter_recipes} skipped)` : ""}</div>
            <div>Supplier specs: <b>{lastSummary.summary?.supplier_specs}</b></div>
            <div>Lots: <b>{lastSummary.summary?.lots}</b></div>
          </div>
        </div>
      )}
    </Card>
  );
}

async function downloadDoc(endpoint, filename) {
  try {
    const token = getStoredToken();
    const resp = await fetch(`${API}${endpoint}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) throw new Error("Server returned " + resp.status);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Downloaded " + filename);
  } catch (err) {
    console.error("Download failed", err);
    toast.error("Download failed — try again");
  }
}

function Field({ label, children, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-stone-500">{label}</Label>
      <div className="mt-1" data-testid={testId}>{children}</div>
    </div>
  );
}

function DangerZoneCard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [working, setWorking] = useState(false);

  const reset = () => {
    setConfirmText("");
    setWorking(false);
  };

  const downloadBundle = (bundle) => {
    try {
      const filename = `AlainesBench_FinalExport_${new Date().toISOString().slice(0, 10)}.json`;
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Final export download failed", e);
    }
  };

  const confirmDelete = async () => {
    if (confirmText !== "DELETE") {
      toast.error("Type DELETE exactly to confirm.");
      return;
    }
    setWorking(true);
    try {
      const { data } = await api.request({
        method: "delete",
        url: "/me/account",
        data: { confirm: "DELETE" },
      });
      // Save the data export to disk before redirecting away.
      if (data?.data_export) downloadBundle(data.data_export);
      // Wipe the client-side auth fallback.
      try { localStorage.removeItem("alaine_access_token"); } catch { /* noop */ }
      toast.success("Account deleted. Your data export was downloaded.", { duration: 6000 });
      setOpen(false);
      // Hard reload so any cached AuthContext state is gone.
      setTimeout(() => { window.location.assign(`${process.env.PUBLIC_URL || ""}/login`); }, 400);
    } catch (err) {
      console.error("Account deletion failed", err);
      const msg = err?.response?.data?.detail || "Deletion failed — try again or email support@alainesbench.com.";
      toast.error(String(msg));
      setWorking(false);
    }
  };

  return (
    <Card className="p-6 border-red-200 mt-6" data-testid="danger-zone-card">
      <div className="flex items-center gap-2 mb-2">
        <Warning size={18} className="text-red-700" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-red-700">Danger Zone</div>
          <h2 className="text-xl font-semibold mt-0.5">Delete account</h2>
        </div>
      </div>
      <p className="text-sm text-stone-600 leading-relaxed">
        Permanently delete your Alaine's Bench account, every formulation, custom ingredient,
        supplier spec, lot record, AI conversation, and snapshot. We'll automatically
        download a final JSON export of everything to your computer on the way out — keep
        that file if you want to migrate later.
      </p>
      <ul className="text-xs text-stone-600 mt-3 space-y-1 list-disc pl-5">
        <li>Active Stripe subscription (if any) is cancelled immediately.</li>
        <li>Payment and audit-trail records are <b>pseudonymised</b>, not deleted, to comply with tax-retention rules — your email and name are wiped from them.</li>
        <li>This cannot be undone. The {user?.email ? <b>{user.email}</b> : "current email"} address becomes free to register again from scratch.</li>
      </ul>
      <div className="mt-5">
        <Button
          onClick={() => { reset(); setOpen(true); }}
          variant="outline"
          className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
          data-testid="open-delete-account-modal"
        >
          <Trash size={14} className="mr-1.5" /> Delete my account…
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!working) { setOpen(v); if (!v) reset(); } }}>
        <DialogContent data-testid="delete-account-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Warning size={20} weight="fill" /> Confirm permanent deletion
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-stone-700 space-y-3">
            <p>
              This will erase <b>{user?.email}</b> and every record tied to it.
              A final JSON export of all your data will download to your
              computer before the account is removed.
            </p>
            <p>
              Type <span className="font-mono bg-stone-100 px-1.5 py-0.5 rounded text-red-700">DELETE</span> below to proceed.
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="font-mono"
              autoFocus
              disabled={working}
              data-testid="delete-account-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setOpen(false); reset(); }}
              disabled={working}
              data-testid="delete-account-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={working || confirmText !== "DELETE"}
              className="bg-red-700 hover:bg-red-600 text-white disabled:opacity-50"
              data-testid="delete-account-confirm"
            >
              {working ? "Deleting…" : "Yes, delete everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
